'use client';

/**
 * Workspace clipboard — copy / cut / paste of nodes + connectors.
 *
 * Wire-format: `application/x-workspace-items+json` with a versioned
 * envelope so older clients can ignore future payload changes (and
 * we can extend the schema without a rolling migration). The same
 * envelope is mirrored as plain text (human summary) so a paste
 * into a non-canvas surface (chat, doc) still does something sensible.
 *
 * The hook returns a stable `WorkspaceClipboard` object that the
 * registry surfaces (menu, shortcuts, toolbar) consume. Internally
 * it sets up THREE listeners on `document`:
 *
 *   - `copy`  — serialise the current selection.
 *   - `cut`   — same, plus delete the source items.
 *   - `paste` — recreate items at the cursor (or 20px offset if the
 *               source workspace is identical).
 *
 * Each listener bails when focus is in an editable surface — the
 * browser's native input/textarea/contenteditable copy still wins.
 * That's the same guard `paste-handler.tsx` already uses; we share
 * it via `isEditableFocus`.
 *
 * The handler is purposefully agnostic about *what* paste fallback
 * fires (image upload, URL link card, plain-text sticky). It only
 * owns the workspace-items round-trip; the existing `PasteHandler`
 * keeps its native fallbacks for media + text.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useReactFlow } from '@xyflow/react';
import type {
  WorkspaceClipboard,
} from '@/lib/workspace/commands';
import type {
  WorkspaceItemDTO,
  WorkspaceItemState,
} from '@/lib/workspaces/types';
import { useWorkspaceActions, useWorkspaceItems, useWorkspace } from './store';
import {
  NO_LIVE_CONTENT_META,
  resolveItemLabel,
  useLiveContentMeta,
  type LiveContentMeta,
} from './item-label';
import {
  useWorkspaceSelection,
  type WorkspaceSelection,
} from './selection-context';
import { isEditableFocus } from './use-workspace-shortcuts';

// ----------------------------------------------------------------------
// Wire format
// ----------------------------------------------------------------------

const CLIPBOARD_MIME = 'application/x-workspace-items+json';

interface ClipboardItemRow {
  // Local id within the payload — connectors reference these so the
  // graph can survive paste even if the destination is a different
  // workspace (or the same workspace where the source items have
  // been deleted by then).
  localId: string;
  type: WorkspaceItemDTO['type'];
  refId: string | null;
  state: WorkspaceItemState;
}

interface ClipboardEnvelope {
  v: 1;
  /** The source workspace id. Same-workspace pastes use this to
   *  decide whether to apply a small offset (so paste-in-place
   *  doesn't drop items exactly on top of the originals). */
  workspaceId: string;
  /** Anchor point captured at copy time (top-left of the selection
   *  bbox). Lets us preserve the relative layout when pasting into
   *  a new workspace OR at a new cursor location. */
  anchor: { x: number; y: number } | null;
  items: ClipboardItemRow[];
  /** Connectors whose endpoints both lived inside the selection,
   *  with their `from` / `to` rewritten to reference `localId`. */
  connectors: ClipboardItemRow[];
}

// ----------------------------------------------------------------------
// Module-level fallback buffer + identity marker
// ----------------------------------------------------------------------
//
// Two failure modes the system clipboard can't cover alone:
//
//   1. Safari / mobile browsers occasionally refuse `ClipboardEvent
//      .setData` for non-text MIME types (the spec restricts what
//      setData accepts outside an explicit user gesture).
//   2. The `navigator.clipboard.write` path for keydown-triggered
//      copies is ASYNCHRONOUS — a fast Cmd+C → Cmd+V can race with
//      the OS write. The keydown shortcut returns before the write
//      lands, so a paste fired one tick later reads stale clipboard.
//
// Mitigation: keep a JS-only buffer that always wins for paste, AND
// stamp every text/plain summary we write with a `WORKSPACE_TAG` +
// per-copy `inMemoryClipboardTag`. On paste, when the system
// clipboard's text/plain still carries our exact tag, we know:
//   - it's OUR content (no external app overwrote it), AND
//   - the in-memory envelope is the source of truth.
//
// When the tag is missing or doesn't match, the clipboard was
// touched by another app since our last copy — we step aside and
// let `paste-handler.tsx` do the right thing with it (image upload,
// URL link card, plain-text sticky).

const WORKSPACE_TAG_PREFIX = '\u200B[workspace-clipboard:';
const WORKSPACE_TAG_SUFFIX = ']\u200B';

let inMemoryClipboard: ClipboardEnvelope | null = null;
let inMemoryClipboardTag: string | null = null;

/**
 * True when the user has an actual (non-collapsed, non-empty) TEXT
 * selection — e.g. highlighting text in a chat message. Selecting text
 * must take precedence over the workspace node-copy: otherwise Cmd+C
 * over text inside a selected node hijacks the copy and writes the
 * node's `[workspace-clipboard:…]` summary instead of the user's text.
 * `isEditableFocus()` only covers inputs/textareas/contenteditable, not
 * selected text in regular (non-editable) DOM like rendered messages.
 */
function hasTextSelection(): boolean {
  if (typeof window === 'undefined') return false;
  const sel = window.getSelection();
  return !!sel && !sel.isCollapsed && sel.toString().trim().length > 0;
}

function generateClipboardTag(): string {
  // Random per-copy identifier. Doesn't need to be cryptographically
  // strong — just collision-resistant across the few copies a user
  // makes per session.
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Build the `text/plain` mirror we write to the system clipboard.
 * Human summary first (what shows up in another app's paste); tag
 * appended in zero-width-space brackets so it's invisible in a
 * paste-into-Slack / Notion / Doc but still letter-for-letter
 * recoverable on the way back.
 *
 * Exported for unit tests — the paste path's round-trip recognition
 * depends on the prefix / suffix never drifting.
 *
 * `live` names the items live-first (see `summarise`). It defaults to "no
 * live metadata", which is the two-argument form the round-trip tests call:
 * the summary then falls back to the names stored on the copied items.
 */
export function stampedSummary(
  env: ClipboardEnvelope,
  tag: string,
  live: LiveContentMeta = NO_LIVE_CONTENT_META,
): string {
  return `${summarise(env, live)} ${WORKSPACE_TAG_PREFIX}${tag}${WORKSPACE_TAG_SUFFIX}`;
}

/**
 * Recover the tag we embedded in a `stampedSummary`. Returns `null`
 * when the text didn't come from us (so the paste path can defer to
 * the external-source pipeline). Exported for unit tests.
 */
export function readClipboardTag(text: string | null | undefined): string | null {
  if (!text) return null;
  const startIdx = text.indexOf(WORKSPACE_TAG_PREFIX);
  if (startIdx < 0) return null;
  const endIdx = text.indexOf(
    WORKSPACE_TAG_SUFFIX,
    startIdx + WORKSPACE_TAG_PREFIX.length,
  );
  if (endIdx < 0) return null;
  return text.slice(startIdx + WORKSPACE_TAG_PREFIX.length, endIdx);
}

// ----------------------------------------------------------------------
// Pure helpers (exported for unit tests)
// ----------------------------------------------------------------------

/**
 * Build a wire envelope from a selection over a workspace's items.
 *
 * Pure function — no React, no DOM. Extracted so the round-trip
 * (serialise → deserialise → rewire connectors) can be unit-tested
 * without mounting the hook.
 *
 * Rules pinned by tests:
 *   - Connectors are only included when BOTH endpoints are in the
 *     selection (otherwise the paste would have a dangling line).
 *   - `localId` is opaque ("c0", "c1", …) — the receiver never
 *     assumes anything about the numeric tail.
 *   - `anchor` is the top-left of the positioned-item bbox, or
 *     `null` when the selection has no positioned items.
 *   - `workspaceId` round-trips so a same-workspace paste can opt
 *     for the 24px nudge instead of stacking on top of the source.
 */
export function serializeSelection(
  items: WorkspaceItemDTO[],
  selection: { nodeIds: ReadonlySet<string>; edgeIds: ReadonlySet<string> },
  workspaceId: string,
): ClipboardEnvelope | null {
  if (selection.nodeIds.size === 0 && selection.edgeIds.size === 0) return null;
  const selectedItems = items.filter(
    (it) => selection.nodeIds.has(it.id) || selection.edgeIds.has(it.id),
  );

  const idMap = new Map<string, string>();
  let counter = 0;
  const nextLocalId = () => `c${counter++}`;

  const nodeRows: ClipboardItemRow[] = [];
  for (const it of selectedItems) {
    if (it.type === 'connector') continue;
    const localId = nextLocalId();
    idMap.set(it.id, localId);
    nodeRows.push({
      localId,
      type: it.type,
      refId: it.refId,
      state: it.state,
    });
  }

  const connectorRows: ClipboardItemRow[] = [];
  for (const it of selectedItems) {
    if (it.type !== 'connector') continue;
    const from = (it.state.cache?.from as string | undefined) ?? null;
    const to = (it.state.cache?.to as string | undefined) ?? null;
    if (!from || !to) continue;
    const fromLocal = idMap.get(from);
    const toLocal = idMap.get(to);
    if (!fromLocal || !toLocal) continue;
    const localId = nextLocalId();
    idMap.set(it.id, localId);
    connectorRows.push({
      localId,
      type: 'connector',
      refId: it.refId,
      state: {
        ...it.state,
        cache: {
          ...(it.state.cache ?? {}),
          from: fromLocal,
          to: toLocal,
        },
      },
    });
  }

  if (nodeRows.length === 0 && connectorRows.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  for (const row of nodeRows) {
    if (row.state.x < minX) minX = row.state.x;
    if (row.state.y < minY) minY = row.state.y;
  }
  const anchor =
    Number.isFinite(minX) && Number.isFinite(minY) ? { x: minX, y: minY } : null;

  return {
    v: 1,
    workspaceId,
    anchor,
    items: nodeRows,
    connectors: connectorRows,
  };
}

/**
 * Apply a localId → real-id remap to an envelope's connectors. Returns
 * a new array (input is not mutated). Connectors whose endpoints aren't
 * in `localToReal` are dropped — the paste shouldn't materialise a
 * connector that points at an item the receiver chose not to recreate.
 */
export function remapConnectorEndpoints(
  connectors: ClipboardItemRow[],
  localToReal: ReadonlyMap<string, string>,
): ClipboardItemRow[] {
  const out: ClipboardItemRow[] = [];
  for (const row of connectors) {
    const from = row.state.cache?.from as string | undefined;
    const to = row.state.cache?.to as string | undefined;
    if (!from || !to) continue;
    const realFrom = localToReal.get(from);
    const realTo = localToReal.get(to);
    if (!realFrom || !realTo) continue;
    out.push({
      ...row,
      state: {
        ...row.state,
        cache: {
          ...(row.state.cache ?? {}),
          from: realFrom,
          to: realTo,
        },
      },
    });
  }
  return out;
}

// ----------------------------------------------------------------------
// Hook
// ----------------------------------------------------------------------

/**
 * Mount the clipboard handler and return a `WorkspaceClipboard`
 * facade usable by command runners (and surfaceable through the
 * menu / shortcut layer).
 *
 * The hook intentionally does NOT take selection / store as args
 * — it reads them through the canvas's existing hooks so a single
 * source of truth drives every copy / cut / paste call regardless
 * of whether the user invoked the gesture via Cmd+C, the menu, or
 * the toolbar.
 */
export function useWorkspaceClipboard(
  /** Same override as `useCommandContext` — required when mounted in
   *  `WorkspaceCanvas` above `WorkspaceSelectionProvider`. */
  selectionOverride?: WorkspaceSelection,
): WorkspaceClipboard {
  const items = useWorkspaceItems();
  const workspace = useWorkspace();
  const contextSelection = useWorkspaceSelection();
  const selection = selectionOverride ?? contextSelection;
  const { addItem, removeItem } = useWorkspaceActions();
  const flow = useReactFlow();
  // Only the plain-text SUMMARY is named live-first (item-label.ts). The
  // envelope still carries each item's state exactly as stored, so a paste
  // recreates the item as it was and this path writes no fresh name into
  // the shared doc (the 2026-09-13 browser pass).
  const live = useLiveContentMeta();

  // Stable mirrors so the event listeners (registered once) always
  // see the latest selection / item set without re-binding on every
  // selection change.
  const itemsRef = useRef(items);
  const selectionRef = useRef(selection);
  const workspaceIdRef = useRef(workspace.id);
  const liveRef = useRef(live);
  itemsRef.current = items;
  selectionRef.current = selection;
  workspaceIdRef.current = workspace.id;
  liveRef.current = live;

  // Cache of the last document-level pointermove. Same purpose as
  // `paste-handler.tsx`'s own `lastPointer` — we use it as the
  // anchor for menu-initiated pastes (where the actual `paste`
  // event arrives with no clipboardData mouse coords).
  const lastPointer = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      lastPointer.current = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    return () => document.removeEventListener('pointermove', onMove);
  }, []);

  // ------------------------------------------------------------------
  // Serialisation helpers
  // ------------------------------------------------------------------

  const buildEnvelope = useCallback((): ClipboardEnvelope | null => {
    return serializeSelection(
      itemsRef.current,
      selectionRef.current,
      workspaceIdRef.current,
    );
  }, []);

  // ------------------------------------------------------------------
  // Native event listeners — the primary copy / cut / paste paths.
  //
  // We register on `document` so a Cmd+C anywhere in the page (with
  // canvas focus) reaches us, but bail out for editable surfaces so
  // the browser's native text copy still works inside a chat input
  // or sticky note.
  // ------------------------------------------------------------------

  useEffect(() => {
    const writeEnvelopeTo = (
      cd: DataTransfer | null,
      env: ClipboardEnvelope,
      tag: string,
    ): boolean => {
      if (!cd) return false;
      try {
        const json = JSON.stringify(env);
        // Internal MIME — primary path.
        cd.setData(CLIPBOARD_MIME, json);
        // Plain-text mirror so a paste into a non-canvas surface
        // (Slack / a chat input / Doc) gets a useful fallback, AND
        // so a paste back into the canvas can prove the clipboard
        // hasn't been touched by another app via the embedded tag.
        cd.setData('text/plain', stampedSummary(env, tag, liveRef.current));
        return true;
      } catch {
        // Browsers occasionally refuse setData for non-text MIME
        // types when the event doesn't come from a direct gesture
        // — fall back to the module-level buffer below.
        return false;
      }
    };

    // CAPTURE phase. Critical: `paste-handler.tsx` also listens on
    // document (bubble) for the URL / file / sticky-note fallback.
    // If both run on a `paste`, the fallback handler reads the
    // `text/plain` summary we wrote for cross-app interop and spawns
    // an extra sticky note alongside the real node paste. Listening
    // in capture lets us call `stopImmediatePropagation` when we
    // own the gesture so the fallback never sees it.
    const onCopy = (e: ClipboardEvent) => {
      // Text selection wins over node copy — let the browser copy the
      // highlighted text (chat messages, etc.) natively.
      if (isEditableFocus() || hasTextSelection()) return;
      const env = buildEnvelope();
      if (!env) return;
      const tag = generateClipboardTag();
      const ok = writeEnvelopeTo(e.clipboardData, env, tag);
      if (ok) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      inMemoryClipboard = env;
      inMemoryClipboardTag = tag;
      // Mirror to the system clipboard via the async API too — this
      // is the only path that survives a tab switch / paste-into-
      // another-tab, since `ClipboardEvent.setData` only populates
      // the synchronous transfer.
      void writeEnvelopeToSystemClipboard(env, tag, liveRef.current);
    };

    const onCut = (e: ClipboardEvent) => {
      if (isEditableFocus() || hasTextSelection()) return;
      const env = buildEnvelope();
      if (!env) return;
      const tag = generateClipboardTag();
      const ok = writeEnvelopeTo(e.clipboardData, env, tag);
      if (ok) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      inMemoryClipboard = env;
      inMemoryClipboardTag = tag;
      void writeEnvelopeToSystemClipboard(env, tag, liveRef.current);
      // Defer deletion to next microtask so the clipboard write
      // settles first — some Safari builds race the two and end
      // up writing an empty clipboard if the source items have
      // already vanished from the DOM when setData runs.
      queueMicrotask(() => {
        const sel = selectionRef.current;
        for (const id of sel.nodeIds) void removeItem(id);
        for (const id of sel.edgeIds) void removeItem(id);
      });
    };

    const onPaste = (e: ClipboardEvent) => {
      if (isEditableFocus()) return;
      const cd = e.clipboardData;
      const raw = cd?.getData(CLIPBOARD_MIME);
      let env: ClipboardEnvelope | null = null;
      if (raw && raw.length > 0) {
        try {
          const parsed = JSON.parse(raw) as ClipboardEnvelope;
          if (parsed?.v === 1) env = parsed;
        } catch {
          // ignore — fall through to memory buffer / native fallback.
        }
      }
      // Memory buffer fallback: only when the system clipboard's
      // text/plain still carries OUR exact tag. That proves the
      // clipboard hasn't been overwritten by another app since the
      // last in-app copy. Common case this matters: the keydown
      // shortcut path fires `navigator.clipboard.write` async — if
      // the user mashes Cmd+V right after Cmd+C, the OS write may
      // not have landed yet, so `raw` is empty. The tag check lets
      // us safely fall through to memory.
      if (!env && cd) {
        const text = cd.getData('text/plain');
        const tag = readClipboardTag(text);
        if (
          inMemoryClipboard &&
          inMemoryClipboardTag &&
          tag === inMemoryClipboardTag
        ) {
          env = inMemoryClipboard;
        }
      }
      if (!env) return; // No internal payload — let `PasteHandler` handle media/text.
      e.preventDefault();
      e.stopImmediatePropagation();
      const anchor = lastPointer.current
        ? flow.screenToFlowPosition(lastPointer.current)
        : null;
      void pasteEnvelope(env, anchor);
    };

    document.addEventListener('copy', onCopy, true);
    document.addEventListener('cut', onCut, true);
    document.addEventListener('paste', onPaste, true);
    return () => {
      document.removeEventListener('copy', onCopy, true);
      document.removeEventListener('cut', onCut, true);
      document.removeEventListener('paste', onPaste, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, removeItem]);

  // ------------------------------------------------------------------
  // Apply a parsed envelope to the live workspace.
  //
  // We honour the selection bbox: every item is repositioned so the
  // bbox top-left lands at `anchor` (or at the source location + a
  // small offset for same-workspace pastes when no cursor anchor is
  // available). Connector endpoints are remapped from `localId` to
  // the freshly-allocated workspace ids that addItem returns.
  // ------------------------------------------------------------------

  const pasteEnvelope = useCallback(
    async (env: ClipboardEnvelope, anchor: { x: number; y: number } | null) => {
      if (env.items.length === 0 && env.connectors.length === 0) return;
      const sameWorkspace = env.workspaceId === workspaceIdRef.current;
      // Translation vector from the envelope's source bbox to the
      // desired drop anchor. If we don't know either, fall back to
      // a small visible offset (24px) for same-workspace pastes and
      // a no-op for cross-workspace pastes.
      let dx = 0;
      let dy = 0;
      if (anchor && env.anchor) {
        dx = anchor.x - env.anchor.x;
        dy = anchor.y - env.anchor.y;
      } else if (sameWorkspace) {
        dx = 24;
        dy = 24;
      }
      // Track localId → newly-allocated server id for connector
      // rewiring. We add nodes first so the connectors below can
      // reference the real ids.
      const localToReal = new Map<string, string>();
      const created: string[] = [];
      for (const row of env.items) {
        const nextState: Partial<WorkspaceItemState> = {
          ...row.state,
          x: (row.state.x ?? 0) + dx,
          y: (row.state.y ?? 0) + dy,
          placement: 'canvas',
          docked: false,
          keptInDock: false,
          parentItemId: null,
          // Reset spawn marker so the new item plays the
          // just-spawned animation rather than inheriting a stale
          // timestamp from the source.
          cache: {
            ...(row.state.cache ?? {}),
            justSpawnedAt: Date.now(),
          },
        };
        try {
          const item = await addItem({
            type: row.type,
            refId: row.refId,
            state: nextState,
            // 'cursor' so collision detection nudges off the exact
            // pixel rather than pulling the widget into the
            // viewport centre.
            placement: 'cursor',
          });
          localToReal.set(row.localId, item.id);
          created.push(item.id);
        } catch {
          // Skip the row — a 4xx (e.g. ref entity revoked) shouldn't
          // abort the whole paste. Connectors that depended on this
          // row will be skipped below when their endpoint lookup
          // misses.
        }
      }
      for (const row of env.connectors) {
        const from = row.state.cache?.from as string | undefined;
        const to = row.state.cache?.to as string | undefined;
        if (!from || !to) continue;
        const realFrom = localToReal.get(from);
        const realTo = localToReal.get(to);
        if (!realFrom || !realTo) continue;
        try {
          await addItem({
            type: 'connector',
            refId: row.refId,
            state: {
              ...row.state,
              cache: {
                ...(row.state.cache ?? {}),
                from: realFrom,
                to: realTo,
              },
            },
          });
        } catch {
          // ignore
        }
      }
      // Auto-focus on the FIRST pasted item so the user can see
      // where the paste landed without an extra pan.
      if (created.length > 0 && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('ws:focus-item', { detail: { itemId: created[0] } }),
        );
      }
    },
    [addItem],
  );

  // ------------------------------------------------------------------
  // Public facade
  //
  // Each method tries the synchronous `document.execCommand` path
  // first (lets the browser route through its built-in copy/cut/
  // paste so OS-level clipboard fills work). If that fails — older
  // browsers, sandboxed iframes — we fall back to writing through
  // the navigator clipboard API.
  // ------------------------------------------------------------------

  const facade = useMemo<WorkspaceClipboard>(() => {
    return {
      hasItems: () => {
        return (
          inMemoryClipboard !== null &&
          (inMemoryClipboard.items.length > 0 ||
            inMemoryClipboard.connectors.length > 0)
        );
      },
      copySelection: async () => {
        const env = buildEnvelope();
        if (!env) return false;
        const tag = generateClipboardTag();
        inMemoryClipboard = env;
        inMemoryClipboardTag = tag;
        await writeEnvelopeToSystemClipboard(env, tag, liveRef.current);
        return true;
      },
      cutSelection: async () => {
        const env = buildEnvelope();
        if (!env) return false;
        const tag = generateClipboardTag();
        inMemoryClipboard = env;
        inMemoryClipboardTag = tag;
        await writeEnvelopeToSystemClipboard(env, tag, liveRef.current);
        const sel = selectionRef.current;
        for (const id of sel.nodeIds) void removeItem(id);
        for (const id of sel.edgeIds) void removeItem(id);
        return true;
      },
      pasteFromClipboard: async (at) => {
        // Try the native clipboard API first — gives us the system
        // payload (which may have come from another tab) AND
        // includes images / text the canvas paste handler should
        // dispatch through its own path. We prefer the internal
        // MIME, fall back to memory when the system clipboard's
        // text/plain still carries our tag.
        let env: ClipboardEnvelope | null = null;
        try {
          if (navigator.clipboard?.read) {
            const records = await navigator.clipboard.read();
            for (const record of records) {
              if (!record.types.includes(CLIPBOARD_MIME)) continue;
              const blob = await record.getType(CLIPBOARD_MIME);
              const text = await blob.text();
              const parsed = JSON.parse(text) as ClipboardEnvelope;
              if (parsed?.v === 1) {
                env = parsed;
                break;
              }
            }
          }
        } catch {
          /* permissions / unsupported MIME — fall through */
        }
        if (!env) {
          // Tag-gated fallback to the in-memory buffer (see paste
          // listener for the same logic mirrored on the sync path).
          try {
            if (navigator.clipboard?.readText) {
              const text = await navigator.clipboard.readText();
              const tag = readClipboardTag(text);
              if (
                inMemoryClipboard &&
                inMemoryClipboardTag &&
                tag === inMemoryClipboardTag
              ) {
                env = inMemoryClipboard;
              }
            } else if (inMemoryClipboard) {
              // readText unavailable (very old Safari) — trust the
              // memory buffer when there's no way to verify.
              env = inMemoryClipboard;
            }
          } catch {
            // permissions denied — last-ditch use of memory buffer.
            if (inMemoryClipboard) env = inMemoryClipboard;
          }
        }
        if (!env) return false;
        await pasteEnvelope(env, at ?? null);
        return true;
      },
    };
  }, [buildEnvelope, pasteEnvelope, removeItem]);

  return facade;
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

/**
 * Write a canvas clipboard envelope to the OS clipboard.
 *
 * Two-tier strategy:
 *
 *   1. `navigator.clipboard.write` with a `ClipboardItem` that
 *      carries both `text/plain` (a human summary) and the custom
 *      `application/x-workspace-items+json` MIME (the full envelope).
 *      This is the only path that gives cross-tab paste of nodes +
 *      connectors with their topology preserved. Chrome 76+,
 *      Edge 79+, Safari 13.1+, Firefox 90+ all support arbitrary
 *      MIME types via `ClipboardItem` (Firefox required the
 *      `dom.events.asyncClipboard.clipboardItem` preference until
 *      v127 — older Firefox falls through to text-only below).
 *
 *   2. Fallback: `writeText` with the summary. The receiver tab
 *      gets a textual fallback (paste into a chat / doc surface
 *      shows the joined item titles) but the canvas-aware tab still
 *      reads from `inMemoryClipboard` on paste, so internal
 *      copy/paste keeps working even when the OS write is degraded.
 *
 * All failures are swallowed — clipboard permissions can be denied
 * by the browser without notice and we never want a clipboard write
 * to surface as an error to the user.
 */
async function writeEnvelopeToSystemClipboard(
  env: ClipboardEnvelope,
  tag: string,
  live: LiveContentMeta,
): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return;
  const summary = stampedSummary(env, tag, live);
  const json = JSON.stringify(env);
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
      const item = new ClipboardItem({
        'text/plain': new Blob([summary], { type: 'text/plain' }),
        [CLIPBOARD_MIME]: new Blob([json], { type: CLIPBOARD_MIME }),
      });
      await navigator.clipboard.write([item]);
      return;
    }
  } catch {
    // Either the MIME type was rejected (older Safari refuses custom
    // MIME types outside an explicit user gesture) or permissions
    // weren't granted. Fall through to the text-only path so at
    // least the summary lands on the clipboard — and crucially the
    // tag, so the paste listener can recognise "this is ours" and
    // re-hydrate from the in-memory buffer.
  }
  try {
    if (navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(summary);
    }
  } catch {
    // Even writeText can throw in Safari private browsing. The
    // in-memory buffer is still the source of truth for canvas
    // copy/paste; this just means cross-tab interop is unavailable.
  }
}

/**
 * Human summary of an envelope: each copied item by NAME, resolved
 * live-first through `resolveItemLabel` (components/workspace/item-label.ts)
 * so a renamed app pastes into chat under the name the rail shows, not the
 * one snapshotted when its window opened (the 2026-09-13 browser pass). A
 * note has no name, so its text stands in before the bare item type.
 */
function summarise(env: ClipboardEnvelope, live: LiveContentMeta): string {
  const labels: string[] = [];
  for (const row of env.items) {
    const cache = (row.state.cache ?? {}) as Record<string, unknown>;
    const text =
      typeof cache.text === 'string' && cache.text.trim() !== ''
        ? cache.text
        : null;
    const name = resolveItemLabel(row, live, text ?? row.type);
    labels.push(name.slice(0, 80));
  }
  if (env.connectors.length > 0) {
    labels.push(`+${env.connectors.length} connector(s)`);
  }
  return labels.length === 0
    ? `Workspace items (×${env.items.length + env.connectors.length})`
    : labels.join(', ');
}

/**
 * Detect whether the given string is a plausible URL. Used by the
 * paste pipeline to choose between "sticky note with the text" vs
 * "spawn a link preview widget". Liberal — anything starting with
 * `http://` / `https://` plus a host counts.
 */
export function looksLikeUrl(s: string): boolean {
  const trimmed = s.trim();
  if (trimmed.length === 0) return false;
  if (!/^https?:\/\//i.test(trimmed)) return false;
  // Single-line only — multi-line text is almost certainly a note,
  // even if the first line happens to be a URL.
  if (/[\n\r]/.test(trimmed)) return false;
  try {
    const u = new URL(trimmed);
    return !!u.hostname;
  } catch {
    return false;
  }
}

/**
 * Dev-only introspection for clipboard debugging. In the browser
 * console: `window.__wsClipboardDebug?.()` after adding
 * `?wsDebug=clipboard` to the URL (wired in workspace-canvas).
 */
export function getWorkspaceClipboardDebugState(): {
  hasEnvelope: boolean;
  itemCount: number;
  connectorCount: number;
  tag: string | null;
} {
  return {
    hasEnvelope: inMemoryClipboard !== null,
    itemCount: inMemoryClipboard?.items.length ?? 0,
    connectorCount: inMemoryClipboard?.connectors.length ?? 0,
    tag: inMemoryClipboardTag,
  };
}
